import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const LibraryDashboard = ({ onNavigate, userInfo }) => {
    // API Data States
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [libraryData, setLibraryData] = useState({
        name: "Loading...",
        libraryId: "...",
        totalBooks: 0,
        issuedBooks: 0,
        availableBooks: 0,
        overdueBooks: 0,
        totalMembers: 0,
        profileImage: null
    });
    const [recentIssues, setRecentIssues] = useState([]);
    const [recentReturns, setRecentReturns] = useState([]);
    const [overdueList, setOverdueList] = useState([]);
    const [notices, setNotices] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Mock Data for Public View
    const mockLibraryData = {
        name: "Library Guest",
        libraryId: "GUEST",
        totalBooks: 12500,
        issuedBooks: 450,
        availableBooks: 12050,
        overdueBooks: 15,
        totalMembers: 3200,
        profileImage: null
    };

    const mockRecentIssues = [
        { bookTitle: "Introduction to Algorithms", memberName: "Rahul Sharma", issueDate: "2024-03-15" },
        { bookTitle: "Clean Code", memberName: "Priya Patel", issueDate: "2024-03-14" },
        { bookTitle: "Artificial Intelligence: A Modern Approach", memberName: "Amit Kumar", issueDate: "2024-03-12" }
    ];

    const mockRecentReturns = [
        { bookTitle: "Operating System Concepts", memberName: "Sneha Gupta", returnDate: "2024-03-15" },
        { bookTitle: "Database System Concepts", memberName: "Vikram Singh", returnDate: "2024-03-13" }
    ];

    const mockOverdueList = [
        { bookTitle: "Computer Networks", memberName: "Rohan Das", dueDate: "2024-03-01", daysOverdue: 14 },
        { bookTitle: "Digital Logic Design", memberName: "Anjali Verma", dueDate: "2024-03-05", daysOverdue: 10 }
    ];

    const mockNotices = [
        { title: "Library Holiday", content: "The library will remain closed on 25th March for Holi.", date: "2024-03-20" },
        { title: "New Arrivals", content: "Check out the new collection of engineering mathematics books.", date: "2024-03-10" }
    ];

    // Fetch Dashboard Data from API or Use Mock Data
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                setError(null);

                console.log('🔍 Starting fetch library dashboard data...');

                // Get token from storage
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                console.log('🔑 Token found:', token ? 'Yes' : 'No');

                if (!token) {
                    console.log('ℹ️ No token found - Using Mock Data for Public Access');
                    // Simulate Loading Delay
                    setTimeout(() => {
                        setLibraryData(mockLibraryData);
                        setRecentIssues(mockRecentIssues);
                        setRecentReturns(mockRecentReturns);
                        setOverdueList(mockOverdueList);
                        setNotices(mockNotices);
                        setLoading(false);
                    }, 800);
                    return;
                }

                // Construct the full API URL
                const apiUrl = `${API_LINK}/api/library/dashboard`;
                console.log('🌐 API URL:', apiUrl);

                const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                console.log('📡 Response status:', response.status);

                if (!response.ok) {
                    console.log('⚠️ API request failed - Using Mock Data');
                    setLibraryData(mockLibraryData);
                    setRecentIssues(mockRecentIssues);
                    setRecentReturns(mockRecentReturns);
                    setOverdueList(mockOverdueList);
                    setNotices(mockNotices);
                } else {
                    const result = await response.json();
                    if (result.success && result.data) {
                        const data = result.data;
                        setLibraryData({
                            name: data.library?.name || "Library Admin",
                            libraryId: data.library?.libraryId || "N/A",
                            totalBooks: data.stats?.totalBooks || 0,
                            issuedBooks: data.stats?.issuedBooks || 0,
                            availableBooks: data.stats?.availableBooks || 0,
                            overdueBooks: data.stats?.overdueBooks || 0,
                            totalMembers: data.stats?.totalMembers || 0,
                            profileImage: data.library?.profileImage || null
                        });
                        setRecentIssues(data.recentIssues || []);
                        setRecentReturns(data.recentReturns || []);
                        setOverdueList(data.overdueList || []);
                        setNotices(data.notices || []);
                    }
                }

            } catch (error) {
                console.error('💥 Error/Fallback:', error);
                // Fallback to mock data on error
                setLibraryData(mockLibraryData);
                setRecentIssues(mockRecentIssues);
                setRecentReturns(mockRecentReturns);
                setOverdueList(mockOverdueList);
                setNotices(mockNotices);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [userInfo]);

    // Handle logout
    const handleLogout = () => {
        sessionStorage.clear();
        localStorage.clear();

        if (onNavigate) {
            onNavigate('home');
        } else {
            window.location.href = '/';
        }
    };

    // Handle navigation for dashboard buttons
    const handleNavigation = (page) => {
        if (onNavigate) {
            switch (page) {
                case 'Books':
                    onNavigate('library-catalog');
                    break;
                case 'Issues':
                    onNavigate('library-issue');
                    break;
                case 'Returns':
                    onNavigate('library-return');
                    break;
                case 'Overdue':
                    onNavigate('library-overdue');
                    break;
                default:
                    console.log(`Navigation to ${page} clicked`);
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Loading Component
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f7fa',
                flexDirection: 'column',
                gap: '1rem'
            }}>
                <div style={{
                    width: '50px',
                    height: '50px',
                    border: '4px solid #e3f2fd',
                    borderTop: '4px solid #238548',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Dashboard...</p>
                <style>
                    {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
                </style>
            </div>
        );
    }

    // Error Component
    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f7fa',
                flexDirection: 'column',
                gap: '1rem'
            }}>
                <div style={{ fontSize: '3rem', color: '#f44336' }}>⚠️</div>
                <p style={{ color: '#666', fontSize: '1.1rem', textAlign: 'center', maxWidth: '400px' }}>
                    {error}
                </p>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: '#238548',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <Layout role="librarian"
            onNavigate={onNavigate}
            activePage="library-dashboard"
            pageTitle="Library Dashboard"
        >
            {/* Welcome Notification Bar */}
            <div style={{
                marginBottom: '20px',
                borderRadius: '8px',
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: '#238548'
            }}>
                <div style={{
                    width: '100%',
                    height: '40px',
                    overflow: 'hidden',
                    backgroundColor: '#238548'
                }}>
                    <div style={{
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        animation: 'marquee 15s linear infinite'
                    }}>
                        <span style={{ marginRight: '10px' }}>📚</span>
                        Welcome to SVU Library Management System! You have {libraryData.overdueBooks} overdue books and {libraryData.issuedBooks} books currently issued.
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <section style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                {/* Total Books Card */}
                <div style={{
                    background: '#ffffff',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease'
                }} onClick={() => handleNavigation('Books')}>
                    <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>📚</div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Total Books</h3>
                    <p style={{
                        fontSize: '2.2rem',
                        fontWeight: 'bold',
                        color: '#238548',
                        margin: '0.5rem 0'
                    }}>
                        {libraryData.totalBooks > 0 ? libraryData.totalBooks.toLocaleString() : '0'}
                    </p>
                    <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
                        In Library Collection
                    </p>
                </div>

                {/* Issued Books Card */}
                <div style={{
                    background: '#ffffff',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease'
                }} onClick={() => handleNavigation('Issues')}>
                    <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>📖</div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Issued Books</h3>
                    <p style={{
                        fontSize: '2.2rem',
                        fontWeight: 'bold',
                        color: '#238548',
                        margin: '0.5rem 0'
                    }}>
                        {libraryData.issuedBooks > 0 ? libraryData.issuedBooks.toLocaleString() : '0'}
                    </p>
                    <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
                        Currently Issued
                    </p>
                </div>

                {/* Available Books Card */}
                <div style={{
                    background: '#ffffff',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    cursor: 'pointer'
                }}>
                    <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>✅</div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Available Books</h3>
                    <p style={{
                        fontSize: '2.2rem',
                        fontWeight: 'bold',
                        color: '#238548',
                        margin: '0.5rem 0'
                    }}>
                        {libraryData.availableBooks > 0 ? libraryData.availableBooks.toLocaleString() : '0'}
                    </p>
                    <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
                        Ready to Issue
                    </p>
                </div>

                {/* Overdue Books Card */}
                <div style={{
                    background: '#ffffff',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    cursor: 'pointer'
                }} onClick={() => handleNavigation('Overdue')}>
                    <div style={{ fontSize: '3rem', color: '#f44336', marginBottom: '1rem' }}>⚠️</div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Overdue Books</h3>
                    <p style={{
                        fontSize: '2.2rem',
                        fontWeight: 'bold',
                        color: '#f44336',
                        margin: '0.5rem 0'
                    }}>
                        {libraryData.overdueBooks > 0 ? libraryData.overdueBooks : '0'}
                    </p>
                    <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
                        Needs Attention
                    </p>
                </div>
            </section>

            {/* Additional Stats Row */}
            <section style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                {/* Total Members Card */}
                <div style={{
                    background: '#ffffff',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    cursor: 'pointer'
                }}>
                    <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>👥</div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Total Members</h3>
                    <p style={{
                        fontSize: '2.2rem',
                        fontWeight: 'bold',
                        color: '#238548',
                        margin: '0.5rem 0'
                    }}>
                        {libraryData.totalMembers > 0 ? libraryData.totalMembers.toLocaleString() : '0'}
                    </p>
                    <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
                        Registered Users
                    </p>
                </div>

                {/* Quick Actions Card */}
                <div style={{
                    background: '#ffffff',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>⚡</span> Quick Actions
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <button
                            onClick={() => handleNavigation('Issues')}
                            style={{
                                padding: '1rem',
                                backgroundColor: '#238548',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            📖 Issue Book
                        </button>
                        <button
                            onClick={() => handleNavigation('Returns')}
                            style={{
                                padding: '1rem',
                                backgroundColor: '#238548',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            🔄 Return Book
                        </button>
                        <button
                            onClick={() => handleNavigation('Books')}
                            style={{
                                padding: '1rem',
                                backgroundColor: '#238548',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            📚 View Catalog
                        </button>
                        <button
                            onClick={() => handleNavigation('Overdue')}
                            style={{
                                padding: '1rem',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            ⚠️ Overdue List
                        </button>
                    </div>
                </div>
            </section>

            {/* Recent Activity Section */}
            <section style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                {/* Recent Issues */}
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        background: '#238548',
                        color: '#ffffff',
                        padding: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                    }}>
                        <span style={{ fontSize: '1.8rem' }}>📖</span>
                        <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Recent Book Issues</h3>
                    </div>
                    <div style={{ padding: '1.5rem' }}>
                        {recentIssues.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {recentIssues.slice(0, 5).map((issue, index) => (
                                    <div key={index} style={{
                                        padding: '1rem',
                                        background: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '1px solid #e9ecef'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <h4 style={{ margin: 0, color: '#333', fontSize: '1rem' }}>{issue.bookTitle}</h4>
                                                <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                                                    Issued to: {issue.memberName}
                                                </p>
                                            </div>
                                            <span style={{ color: '#888', fontSize: '0.85rem' }}>{formatDate(issue.issueDate)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <div style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }}>📖</div>
                                <p style={{ color: '#666', fontSize: '1rem' }}>No recent book issues</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent Returns */}
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        background: '#238548',
                        color: '#ffffff',
                        padding: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                    }}>
                        <span style={{ fontSize: '1.8rem' }}>🔄</span>
                        <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Recent Book Returns</h3>
                    </div>
                    <div style={{ padding: '1.5rem' }}>
                        {recentReturns.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {recentReturns.slice(0, 5).map((returnItem, index) => (
                                    <div key={index} style={{
                                        padding: '1rem',
                                        background: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '1px solid #e9ecef'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <h4 style={{ margin: 0, color: '#333', fontSize: '1rem' }}>{returnItem.bookTitle}</h4>
                                                <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                                                    Returned by: {returnItem.memberName}
                                                </p>
                                            </div>
                                            <span style={{ color: '#888', fontSize: '0.85rem' }}>{formatDate(returnItem.returnDate)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <div style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }}>🔄</div>
                                <p style={{ color: '#666', fontSize: '1rem' }}>No recent book returns</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Overdue Books Alert Section */}
            <section style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        background: '#f44336',
                        color: '#ffffff',
                        padding: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={{ fontSize: '1.8rem' }}>⚠️</span>
                            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Overdue Books Alert</h3>
                        </div>
                        <button
                            onClick={() => handleNavigation('Overdue')}
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            View All Overdue
                        </button>
                    </div>
                    <div style={{ padding: '1.5rem' }}>
                        {overdueList.length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                            <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Book Title</th>
                                            <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Member</th>
                                            <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Due Date</th>
                                            <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Days Overdue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overdueList.slice(0, 5).map((item, index) => (
                                            <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                                                <td style={{ padding: '1rem' }}>{item.bookTitle}</td>
                                                <td style={{ padding: '1rem' }}>{item.memberName}</td>
                                                <td style={{ padding: '1rem' }}>{formatDate(item.dueDate)}</td>
                                                <td style={{ padding: '1rem', color: '#f44336', fontWeight: 'bold' }}>
                                                    {item.daysOverdue} days
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <div style={{ fontSize: '3rem', color: '#4caf50', marginBottom: '1rem' }}>✅</div>
                                <p style={{ color: '#666', fontSize: '1.1rem' }}>No overdue books. Great job!</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Notices Section */}
            {notices.length > 0 && (
                <section style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: '1.5rem',
                    marginBottom: '2rem'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            background: '#238548',
                            color: '#ffffff',
                            padding: '1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem'
                        }}>
                            <span style={{ fontSize: '1.8rem' }}>📢</span>
                            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Library Notices</h3>
                        </div>
                        <div style={{ padding: '1.5rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {notices.map((notice, index) => (
                                    <div key={index} style={{
                                        padding: '1rem',
                                        background: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '1px solid #e9ecef',
                                        borderLeft: '4px solid #238548'
                                    }}>
                                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{notice.title}</h4>
                                        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>{notice.content}</p>
                                        <span style={{ color: '#888', fontSize: '0.8rem' }}>{formatDate(notice.date)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </Layout>
    );
};

export default LibraryDashboard;
